import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CandlestickSeries,
  createChart,
  TickMarkType,
  type IChartApi,
  type ISeriesApi,
  type Time,
  type UTCTimestamp,
} from 'lightweight-charts';
import type { CandlesResponse, Timeframe } from '@contracts/market';
import { api } from '../lib/api';
import { useLive } from '../store/live';

const COLORS = {
  bg: '#fcf9f1',
  text: '#4c3f34',
  grid: '#efe5cf',
  rule: '#ddd0b4',
  up: '#556332',
  down: '#7a2b38',
};

// The backend sends UTC epoch seconds; everything below renders in the viewer's local timezone.
const toDate = (t: Time): Date => new Date((t as number) * 1000);
const hm = (d: Date) => d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });

/** Axis labels: time of day, with the date on day/month/year boundaries. */
export function tickLabel(t: Time, type: TickMarkType): string {
  const d = toDate(t);
  switch (type) {
    case TickMarkType.Year:
      return String(d.getFullYear());
    case TickMarkType.Month:
      return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
    case TickMarkType.DayOfMonth:
      return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
    default:
      return hm(d);
  }
}

/** Crosshair label: full local date and time. */
export function crosshairLabel(t: Time): string {
  const d = toDate(t);
  return `${d.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short' })} ${hm(d)}`;
}

export function CandleChart({ timeframe }: { timeframe: Timeframe }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const lastTimeRef = useRef<number>(0);
  const live = useLive((s) => s.candles[timeframe]);
  const precision = useLive((s) => s.provider?.instrument?.pricePrecision);

  const query = useQuery({
    queryKey: ['candles', timeframe],
    queryFn: () => api.get<CandlesResponse>(`/api/v1/market/candles?timeframe=${timeframe}&limit=500`),
    refetchInterval: 60_000,
  });
  const digits = query.data?.pricePrecision ?? precision ?? 2;
  const tick = query.data?.tickSize ?? 10 ** -digits;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const chart = createChart(el, {
      autoSize: true,
      layout: {
        background: { color: COLORS.bg },
        textColor: COLORS.text,
        fontFamily: "'Source Sans 3', system-ui, sans-serif",
        attributionLogo: true,
      },
      grid: { vertLines: { color: COLORS.grid }, horzLines: { color: COLORS.grid } },
      rightPriceScale: { borderColor: COLORS.rule },
      timeScale: { borderColor: COLORS.rule, timeVisible: true, secondsVisible: false, tickMarkFormatter: tickLabel },
      localization: { timeFormatter: crosshairLabel },
    });
    const series = chart.addSeries(CandlestickSeries, {
      upColor: COLORS.up,
      downColor: COLORS.down,
      borderUpColor: COLORS.up,
      borderDownColor: COLORS.down,
      wickUpColor: COLORS.up,
      wickDownColor: COLORS.down,
    });
    chartRef.current = chart;
    seriesRef.current = series;
    return () => {
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, []);

  // Price scale follows the instrument (e.g. 3 decimals / 0.001 tick for MT5 XAUUSD.sml).
  useEffect(() => {
    seriesRef.current?.applyOptions({ priceFormat: { type: 'price', precision: digits, minMove: tick } });
  }, [digits, tick]);

  useEffect(() => {
    const series = seriesRef.current;
    if (!series || !query.data) return;
    const data = query.data.candles
      .map((c) => ({ time: c.time as UTCTimestamp, open: c.open, high: c.high, low: c.low, close: c.close }))
      .sort((a, b) => (a.time as number) - (b.time as number));
    series.setData(data);
    lastTimeRef.current = data.length ? (data[data.length - 1]!.time as number) : 0;
    if (data.length) chartRef.current?.timeScale().scrollToRealTime();
  }, [query.data]);

  // Live updates of the forming candle (and newly closed ones) from the market stream.
  useEffect(() => {
    const series = seriesRef.current;
    if (!series || !live || live.timeframe !== timeframe || !query.data) return;
    if (live.time < lastTimeRef.current) return;
    series.update({ time: live.time as UTCTimestamp, open: live.open, high: live.high, low: live.low, close: live.close });
    lastTimeRef.current = live.time;
  }, [live, timeframe, query.data]);

  const candles = query.data?.candles ?? [];
  const last = candles[candles.length - 1];
  let overlay: string | null = null;
  if (query.isLoading) overlay = 'Loading historical candles…';
  else if (query.isError) overlay = 'Historical candles are unavailable. Retrying…';
  else if (!candles.length) overlay = 'No candles yet — waiting for the provider’s history.';

  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return (
    <div>
      <div
        className="chart"
        ref={containerRef}
        role="img"
        aria-label={`${timeframe} candlestick chart for XAUUSD${last ? `, last close ${last.close.toFixed(digits)}` : ''}`}
        data-testid="candle-chart"
      >
        {overlay ? (
          <div className="chart__overlay" role="status">
            {overlay}
          </div>
        ) : null}
      </div>
      <p className="small muted" style={{ marginTop: 6 }} data-testid="chart-caption">
        {query.data?.dataSource === 'replay' ? 'REPLAY DATA (deterministic, not live prices) · ' : ''}
        Bid prices, {digits} decimals (same basis as the MT5 terminal) · times in your timezone ({zone}).
      </p>
    </div>
  );
}

export function TimeframeSelector({ value, onChange }: { value: Timeframe; onChange: (tf: Timeframe) => void }) {
  const options: Timeframe[] = ['1m', '5m', '15m', '1h'];
  return (
    <div className="segmented" role="group" aria-label="Chart timeframe">
      {options.map((tf) => (
        <button key={tf} type="button" aria-pressed={value === tf} onClick={() => onChange(tf)}>
          {tf}
        </button>
      ))}
    </div>
  );
}
